import type { PermissionRequest } from "../types/permission.ts";

export type RequestAttributionInput = {
  /** The identity that initiated the request */
  requestor: { type: "agent" | "user"; sub: string };
  /** Subjects of the agents the request was delegated through */
  viaAgents: string[];
  /** The principal receiving access, when different from the requestor */
  onBehalfOf?: string;
};

/** Splits a request's acting identities into display parts
 *
 * Single source of truth for "who requested this" attribution, shared by the
 * notifier renderers (Slack / Teams / email) and the web UI so they always
 * agree. When the requestor is unknown, the request was made by a headless
 * agent: the first agent is the requestor and any remaining agents are
 * delegates. Otherwise all agents are delegates acting for the user.
 *
 * A headless agent (`agentic.headless`) authenticates as itself, so when
 * it's the *only* agent in the chain, that entry just restates the
 * requestor and is dropped. When there's more than one agent, the chain
 * isn't well-understood enough yet to say which entries (if any) are
 * redundant, so it's left as-is for now.
 */
export const toRequestAttribution = (
  request: Pick<PermissionRequest, "agentic" | "principal" | "requestor">
): RequestAttributionInput => {
  const { agentic, principal, requestor } = request;
  const agents = agentic?.agents ?? [];
  return {
    onBehalfOf: requestor !== principal ? principal : undefined,
    requestor: {
      sub: requestor,
      type: agentic?.headless ? "agent" : "user",
    },
    viaAgents:
      agentic?.headless && agents.length === 1
        ? []
        : agents.map(({ sub }) => sub),
  };
};
